import api from "./api";
import axios from "axios";

export const serverAddress = (process.env?.REACT_APP_BASE_URL || "").trim();

export const getLocalSessionToken = () => {
  if (typeof globalThis === "undefined") return undefined;
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    undefined
  );
};

export const getAuthBaseUrl = () => {
  const base = serverAddress;
  if (!base) return "/oauth/auth";
  if (base.endsWith("/oauth/auth")) return base;
  if (base.endsWith("/auth")) return base;
  if (base.includes("localhost:8090")) return `${base}/auth`;
  return `${base}/oauth/auth`;
};

export const getAuthHeaders = () => {
  const token = getLocalSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getAllRolesPermissions = async () => {
  try {
    const res = await axios.get(`${getAuthBaseUrl()}/roles/2/permissions`, {
      headers: getAuthHeaders(),
    });
    return res;
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
};

export const getRoles = async () => {
  try {
    const res = await axios.get(`${getAuthBaseUrl()}/roles/2`, {
      headers: getAuthHeaders(),
    });
    return res;
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
};

export const searchDirectoryUsers = async (query) => {
  const q = typeof query === "string" ? query.trim() : "";
  if (q.length < 3 || q.length > 100) {
    return { error: "Query must be 3–100 characters." };
  }
  try {
    const res = await axios.get(
      `${getAuthBaseUrl()}/applications/2/directory/users`,
      {
        params: { query: q },
        headers: getAuthHeaders(),
      },
    );
    return res.data;
  } catch (e) {
    const msg =
      e.response?.data?.message || (e instanceof Error ? e.message : String(e));
    return { error: msg, status: e.response?.status, data: e.response?.data };
  }
};

export const addUserWithRole = async ({ directoryUserId, roleId }) => {
  if (!directoryUserId) {
    return { error: "directoryUserId is required" };
  }
  if (!roleId) {
    return { error: "roleId is required" };
  }
  try {
    const res = await axios.post(
      `${getAuthBaseUrl()}/applications/2/users`,
      {
        directoryUserId,
        roleId: Number(roleId),
      },
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      },
    );
    return res.data;
  } catch (e) {
    const msg =
      e.response?.data?.message || (e instanceof Error ? e.message : String(e));
    return {
      error: msg,
      status: e.response?.status,
      data: e.response?.data,
    };
  }
};

const EMPLOYEES_BASE = "/api/employees";

export const getAllRoles = async () => {
  try {
    const response = await api.get(EMPLOYEES_BASE);
    return response.data;
  } catch (error) {
    console.error("Unexpected error", error);
    return [
      { id: 1, name: "Alice", role: "Super Admin" },
      { id: 2, name: "Bob", role: "Viewer" },
    ];
  }
};

export const updateRole = async (employeeId, payload) => {
  if (!employeeId) return "employeeId required";
  try {
    const response = await api.put(
      `${EMPLOYEES_BASE}/${employeeId}/role`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Unexpected error", error);
    return { id: employeeId, updated: true, ...payload };
  }
};

export const updateEmployeeApplicationRoles = async (
  employeeId,
  applicationId,
  payload,
) => {
  if (!employeeId) return "employeeId required";
  if (!applicationId) return "applicationId required";

  // Normalize payload: if roles array, map to roleIds
  let body = { ...payload };
  if (!body.roleIds && Array.isArray(body.roles)) {
    body.roleIds = body.roles.map((r) => r.id).filter(Boolean);
  }
  if (body.roleId && !body.roleIds) {
    body.roleIds = [body.roleId];
  }

  try {
    const response = await api.put(
      `${EMPLOYEES_BASE}/${employeeId}/${applicationId}`,
      body,
    );
    return response.data;
  } catch (error) {
    console.error("Unexpected error", error);
    return {
      employeeId,
      applicationId,
      updated: false,
      error: error?.response?.data?.message || error.message,
      echo: body,
    };
  }
};

export const assignOrEditRoles = async (employeeId, applicationId, roleIds) => {
  if (!Array.isArray(roleIds) || roleIds.length === 0) {
    return "roleIds (non-empty array) required";
  }
  return updateEmployeeApplicationRoles(employeeId, applicationId, { roleIds });
};

// --------------------------------------------------------------
// Helper: Convert string status to numeric (1 for enable, 0 otherwise)
// --------------------------------------------------------------
const stringToStatus = (str) => {
  if (str.toLowerCase() === "enable") {
    return 1;
  }
  return 0;
};

// --------------------------------------------------------------
// Helper: Extract status value from object payload
// --------------------------------------------------------------
const getStatusFromObject = (statusPayload) => {
  const innerStatus = statusPayload.status;
  const innerType = typeof innerStatus;
  if (innerType === "string") {
    return stringToStatus(innerStatus);
  }
  if (innerType === "number") {
    return innerStatus;
  }
  return null;
};

// --------------------------------------------------------------
// Helper: Extract status value from payload, returns number or null
// --------------------------------------------------------------
const getStatusValue = (statusPayload) => {
  const payloadType = typeof statusPayload;

  if (payloadType === "string") {
    return stringToStatus(statusPayload);
  }
  if (payloadType === "number") {
    return statusPayload;
  }
  if (statusPayload && payloadType === "object") {
    return getStatusFromObject(statusPayload);
  }
  return null;
};

// --------------------------------------------------------------
// Helper: Get error message for invalid status payload
// --------------------------------------------------------------
const getStatusError = (statusPayload, statusValue) => {
  if (statusValue !== null) {
    return null;
  }
  if (statusPayload && typeof statusPayload === "object") {
    return "status in payload must be string or number";
  }
  return "statusPayload (string, number, or object) required";
};

// --------------------------------------------------------------
// New: update status for an employee within a specific application
// Endpoint: /api/employees/:employeeId/:applicationId/status (PUT)
// Accepts either a string status or an object { status: "Enable" }
export const updateEmployeeApplicationStatus = async (
  employeeId,
  applicationId,
  statusPayload,
) => {
  if (!employeeId) return "employeeId required";
  if (!applicationId) return "applicationId required";

  // Normalize payload to the required shape:
  // Enable -> { status: 1 }, Disable -> { status: 0 }
  const statusValue = getStatusValue(statusPayload);
  const error = getStatusError(statusPayload, statusValue);
  if (error) {
    return error;
  }

  const body = { status: statusValue };

  try {
    const response = await api.patch(
      `${EMPLOYEES_BASE}/${employeeId}/${applicationId}/status`,
      body,
    );
    return response.data;
  } catch (error) {
    return {
      employeeId,
      applicationId,
      updated: false,
      error: error?.response?.data?.message || error.message,
      echo: body,
    };
  }
};

export const addPermissions = async (
  applicationId,
  permissionsPayload,
  token,
) => {
  if (!applicationId) return "applicationId required";
  if (!Array.isArray(permissionsPayload))
    return "permissionsPayload must be an array";
  const url = `${serverAddress}/oauth/auth/addPermissions`;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  try {
    const res = await axios.post(url, permissionsPayload, {
      params: { applicationId },
      headers,
    });
    return res.data;
  } catch (error) {
    return {
      applicationId,
      updated: false,
      error: error?.response?.data?.message || error.message,
      echo: permissionsPayload,
    };
  }
};
